import { Platform } from "react-native"

import { type ElevationKey, type ElevationStep } from "../theme/tokens/elevation"

/**
 * Ce que `shadowStyle` a besoin de lire dans le thème : la marche, et l'encre de l'ombre. Écrit
 * en forme minimale plutôt qu'en `AppTheme`, parce que les thèmes de persona repointent des
 * jetons et ne sont donc pas assignables au socle.
 */
type ShadowTheme = {
    colors: { shadow: string }
    elevation: Record<ElevationKey, ElevationStep>
}

/** Une seule forme, toutes clés facultatives : une union de deux objets élargit l'inférence d'Unistyles. */
type ShadowStyle = {
    boxShadow?: string
    shadowColor?: string
    shadowOffset?: { width: number; height: number }
    shadowOpacity?: number
    shadowRadius?: number
    elevation?: number
}

/** Une couleur `#RRGGBB` à cette opacité, en `rgba(…)` ; `null` pour toute autre forme (une variable CSS au web). */
export function hexWithAlpha(hex: string, alpha: number): string | null {
    const match = /^#([0-9a-f]{6})$/i.exec(hex)
    if (!match) return null
    const value = parseInt(match[1], 16)
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}

function withAlpha(hex: string, alpha: number): string {
    // Sur le web, Unistyles sert les couleurs du thème en variables CSS (`var(--colors-…)`) : pas
    // d'hexadécimal à découper, c'est `color-mix` qui pose l'opacité.
    return hexWithAlpha(hex, alpha) ?? `color-mix(in srgb, ${hex} ${Math.round(alpha * 100)}%, transparent)`
}

/**
 * L'ombre d'une surface, écrite pour la plateforme.
 *
 * Le composant ne donne que le NOM de la marche : la couleur se lit ici, dans le jeton `shadow`.
 * Le livre ne connaît pas de gris neutre — une ombre se peint dans l'encre du texte, et sur la
 * nuit celle-ci passe au noir, où elle disparaît (une carte sombre se lit à sa surface). Laisser
 * l'appelant passer la couleur, c'était lui laisser l'occasion d'en passer une autre.
 *
 * Sur le web, Unistyles sert la couleur en variable CSS et ne peut pas lui appliquer
 * `shadowOpacity` : les quatre `shadow*` sortaient en `box-shadow` à la couleur PLEINE, donc une
 * ombre marine opaque au lieu de 8 %. On y écrit donc `boxShadow`, l'opacité portée par la
 * couleur. Le natif garde `shadow*` + `elevation`.
 */
export function shadowStyle(theme: ShadowTheme, level: ElevationKey): ShadowStyle {
    const { offsetY, opacity, radius, android } = theme.elevation[level]
    const color = theme.colors.shadow

    if (Platform.OS === "web") {
        return { boxShadow: opacity === 0 ? "none" : `0px ${offsetY}px ${radius}px ${withAlpha(color, opacity)}` }
    }
    return {
        shadowColor: color,
        shadowOffset: { width: 0, height: offsetY },
        shadowOpacity: opacity,
        shadowRadius: radius,
        elevation: android,
    }
}
